
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { BarChart3, Users, Shield, TrendingUp, Settings, Eye } from 'lucide-react';
import { AppSettingsService } from '@/services/app-settings-service';

interface MarketAnalysisStats {
  total_affiliate_users: number;
  total_referred_users: number;
  daily_active_users: number;
  total_analysis_sessions: number;
}

export const MarketAnalysisSettingsManagement = () => {
  const queryClient = useQueryClient();
  const [isUpdating, setIsUpdating] = useState(false);

  // Fetch current settings
  const { data: settings, isLoading: isLoadingSettings } = useQuery({
    queryKey: ['marketAnalysisSettings'],
    queryFn: async () => {
      const affiliateOnlyMode = await AppSettingsService.getSetting('market_analysis_affiliate_only');
      const adminOverride = await AppSettingsService.getSetting('market_analysis_admin_override');
      
      return {
        affiliate_only_mode: affiliateOnlyMode === 'true',
        admin_override_enabled: adminOverride === 'true'
      };
    },
  });

  // Fetch analytics data
  const { data: analytics, isLoading: isLoadingAnalytics } = useQuery({
    queryKey: ['marketAnalysisAnalytics'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_market_analysis_stats');
      
      if (error) {
        console.error('Error fetching market analysis stats:', error);
        return {
          total_affiliate_users: 0,
          total_referred_users: 0,
          daily_active_users: 0,
          total_analysis_sessions: 0
        };
      }
      
      return data as MarketAnalysisStats;
    },
  });

  // Update settings mutation
  const updateSettingMutation = useMutation({
    mutationFn: async ({ key, value }: { key: string; value: string }) => {
      const { error } = await supabase
        .from('app_settings')
        .upsert({ 
          setting_key: key, 
          setting_value: value,
          updated_at: new Date().toISOString()
        }, { 
          onConflict: 'setting_key' 
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketAnalysisSettings'] });
      toast.success('Settings updated successfully');
    },
    onError: (error) => {
      console.error('Error updating settings:', error);
      toast.error('Failed to update settings');
    },
  });

  const handleToggleAffiliateOnly = async (enabled: boolean) => {
    setIsUpdating(true);
    try {
      await updateSettingMutation.mutateAsync({
        key: 'market_analysis_affiliate_only',
        value: enabled.toString()
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleToggleAdminOverride = async (enabled: boolean) => {
    setIsUpdating(true);
    try {
      await updateSettingMutation.mutateAsync({
        key: 'market_analysis_admin_override',
        value: enabled.toString()
      });
    } finally {
      setIsUpdating(false);
    }
  };

  if (isLoadingSettings) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Access Control Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Market Analysis Access Control
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label htmlFor="affiliate-only">Affiliate-Only Mode</Label>
              <p className="text-sm text-muted-foreground">
                Restrict Market Analysis access to affiliate customers and active affiliates only
              </p>
            </div>
            <Switch
              id="affiliate-only"
              checked={settings?.affiliate_only_mode || false}
              onCheckedChange={handleToggleAffiliateOnly}
              disabled={isUpdating}
            />
          </div>

          <Separator />

          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label htmlFor="admin-override">Admin Override</Label>
              <p className="text-sm text-muted-foreground">
                Allow admins to bypass affiliate restrictions and access Market Analysis
              </p>
            </div>
            <Switch
              id="admin-override"
              checked={settings?.admin_override_enabled || false}
              onCheckedChange={handleToggleAdminOverride}
              disabled={isUpdating}
            />
          </div>

          <div className="bg-muted/50 p-4 rounded-lg">
            <div className="flex items-start gap-3">
              <Eye className="h-5 w-5 text-blue-500 mt-0.5" />
              <div>
                <h4 className="font-medium text-sm">Current Status</h4>
                <p className="text-sm text-muted-foreground mt-1">
                  Market Analysis is currently{' '}
                  <Badge variant={settings?.affiliate_only_mode ? "destructive" : "default"}>
                    {settings?.affiliate_only_mode ? 'Restricted to Affiliates' : 'Open to All Users'}
                  </Badge>
                  {settings?.admin_override_enabled && (
                    <> with <Badge variant="secondary">Admin Override Enabled</Badge></>
                  )}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Analytics Dashboard */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Market Analysis Usage Analytics
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoadingAnalytics ? (
            <div className="flex items-center justify-center p-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 p-4 rounded-lg border border-emerald-500/20">
                <div className="flex items-center gap-3">
                  <Users className="h-8 w-8 text-emerald-500" />
                  <div>
                    <p className="text-sm text-muted-foreground">Active Affiliates</p>
                    <p className="text-2xl font-bold">{analytics?.total_affiliate_users || 0}</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-500/10 to-blue-500/5 p-4 rounded-lg border border-blue-500/20">
                <div className="flex items-center gap-3">
                  <TrendingUp className="h-8 w-8 text-blue-500" />
                  <div>
                    <p className="text-sm text-muted-foreground">Referred Users</p>
                    <p className="text-2xl font-bold">{analytics?.total_referred_users || 0}</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-purple-500/10 to-purple-500/5 p-4 rounded-lg border border-purple-500/20">
                <div className="flex items-center gap-3">
                  <Eye className="h-8 w-8 text-purple-500" />
                  <div>
                    <p className="text-sm text-muted-foreground">Daily Active</p>
                    <p className="text-2xl font-bold">{analytics?.daily_active_users || 0}</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-amber-500/10 to-amber-500/5 p-4 rounded-lg border border-amber-500/20">
                <div className="flex items-center gap-3">
                  <BarChart3 className="h-8 w-8 text-amber-500" />
                  <div>
                    <p className="text-sm text-muted-foreground">Analysis Sessions</p>
                    <p className="text-2xl font-bold">{analytics?.total_analysis_sessions || 0}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
